/**
 * @fileoverview Venue Form component.
 * @summary Form for creating and editing venues.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/VenueForm
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faEdit, faMagic } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import FloatingLabelInput from '../../../components/FloatingLabelInput'
import Spinner from '../../../components/Spinner'
import type { Venue } from '../../../store/slices/venuesSlice'

interface VenueFormProps {
  isOpen: boolean
  editingVenue: Venue | null
  formData: {
    name: string
    address: string
    city: string
    country: string
    googleMapsUrl: string
  }
  isSaving: boolean
  error: string | null
  onFormDataChange: (data: Partial<VenueFormProps['formData']>) => void
  onGenerateMapsUrl: () => void
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
}

/**
 * @summary Venue form component.
 * @param {VenueFormProps} props - Component props.
 * @returns {React.ReactElement} Venue form or null if not open.
 */
export function VenueForm({
  isOpen,
  editingVenue,
  formData,
  isSaving,
  error,
  onFormDataChange,
  onGenerateMapsUrl,
  onSubmit,
  onCancel,
}: VenueFormProps) {
  const { t } = useTranslation()

  if (!isOpen) {
    return null
  }

  return (
    <div className="card mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-(--color-text-primary)">
          {editingVenue ? t('venues.editVenue') : t('venues.addVenue')}
        </h2>
        <button
          onClick={onCancel}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
          aria-label={t('common.close')}
        >
          <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="alert alert-error mb-4">
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 m-0">
          <FloatingLabelInput
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={(e) => onFormDataChange({ name: e.target.value })}
            label={t('venues.name')}
            required
          />
          <FloatingLabelInput
            id="city"
            name="city"
            type="text"
            value={formData.city}
            onChange={(e) => onFormDataChange({ city: e.target.value })}
            label={t('venues.city')}
            required
          />
        </div>

        <FloatingLabelInput
          id="address"
          name="address"
          type="text"
          value={formData.address}
          onChange={(e) => onFormDataChange({ address: e.target.value })}
          label={t('venues.address')}
          optional
        />

        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 m-0">
          <FloatingLabelInput
            id="country"
            name="country"
            type="text"
            value={formData.country}
            onChange={(e) => onFormDataChange({ country: e.target.value })}
            label={t('venues.country')}
            optional
          />

          <FloatingLabelInput
            id="googleMapsUrl"
            name="googleMapsUrl"
            type="url"
            value={formData.googleMapsUrl}
            onChange={(e) => onFormDataChange({ googleMapsUrl: e.target.value })}
            label={t('venues.googleMapsUrl')}
            optional
          />
        </div>

        <div className="w-full flex justify-center m-0">
          <button
            type="button"
            onClick={onGenerateMapsUrl}
            className="btn btn-outline btn-sm w-full px-2 my-0 mx-auto"
            title={'Click : ' + t('venues.generateMapsUrl')}
            aria-label={t('venues.generateMapsUrl')}
          >
            <FontAwesomeIcon icon={faMagic} className="w-4 h-4 -mr-1" />
            {t('venues.generateMapsUrl')}
          </button>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isSaving}
          >
            {t('venues.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner size="sm" variant="white" />
                <span className="ml-2">{t('venues.saving')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEdit} className="w-4 h-4 mr-2" />
                {t('venues.save')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

